/**
 * ProtectedRoute Component - RBAC wrapper for admin-only routes
 * 
 * PRIV-25.2: Guards routes based on JWT role claims
 * 
 * Usage:
 * <ProtectedRoute roles={['verascore_admin', 'auditor']}>
 *   <PrivacyKpiDashboard />
 * </ProtectedRoute>
 */

import { FC, ReactNode } from 'react';
import { useHasRole } from '../../hooks/useHasRole';

interface ProtectedRouteProps {
  /** Array of roles that are allowed to access this route */
  roles: string[];
  /** Content to render if user has access */
  children: ReactNode;
  /** Optional custom fallback for unauthorized users (default: 403 page) */
  fallback?: ReactNode;
}

/**
 * Default unauthorized component shown when user lacks required role
 */
const UnauthorizedPage: FC = () => {
  const handleGoBack = () => {
    window.history.back();
  };

  const handleGoHome = () => {
    window.history.pushState({}, '', '?view=');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center max-w-md mx-auto p-8">
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
          <svg
            className="w-8 h-8 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
        <p className="text-gray-600 mb-6">
          You don't have permission to access this page. This area is restricted to
          authorized administrators and auditors only.
        </p>
        <p className="text-sm text-gray-500 mb-6">
          Error Code: 403 Forbidden
        </p>
        <div className="flex gap-4 justify-center">
          <button
            onClick={handleGoBack}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Go Back
          </button>
          <button
            onClick={handleGoHome}
            className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * ProtectedRoute - Renders children only if user has required role
 * 
 * @param roles - Array of allowed roles (user must have at least one)
 * @param children - Content to render for authorized users
 * @param fallback - Optional custom unauthorized component
 */
export const ProtectedRoute: FC<ProtectedRouteProps> = ({
  roles,
  children,
  fallback,
}) => {
  const hasAccess = useHasRole(roles);

  console.log('[ProtectedRoute] Access check:', { roles, hasAccess });

  if (!hasAccess) {
    return <>{fallback || <UnauthorizedPage />}</>;
  }

  return <>{children}</>;
};

export default ProtectedRoute;

